import { IFilter } from "interfaces/filter/IFilter"
import { IIntervalValue } from "interfaces/filter/IIntervalValue";
import { LocationQuery, useRoute, useRouter } from "vue-router"

export const useQuery = () => {
  const router = useRouter();
  const route  = useRoute();

  const mapQueriesToFilter = (query: LocationQuery) => {
    const intervalFilters: IFilter[] = []
    const dropdownFilters: IFilter[] = []

    for (const [rawKey, rawVal] of Object.entries(query)) {
      if (!rawVal || typeof rawVal !== 'string') {
        continue;
      }
    
      const num = parseFloat(rawVal);

      // Is it an interval filter?
      if (rawKey.endsWith('_min') || rawKey.endsWith('_max')) {
        const baseKey = rawKey.replace(/_(min|max)$/, '')
        const side    = rawKey.endsWith('_min') ? 'min' : 'max'

        // find or init an IFilter for this dataKey
        let intervalFilter = intervalFilters.find(f => f.dataKey === baseKey)
        if (!intervalFilter) {
          intervalFilter = { 
            dataKey: baseKey,
            value: {
              min: null, max: null 
            }
          };
          intervalFilters.push(intervalFilter);
        }

        (intervalFilter.value as IIntervalValue)[side] = isNaN(num) ? null : num;
      }
      else {
        // A string/array filter
        // Parse dataKey as string or string[]
        const keyOrKeys = rawKey.includes(',')
          ? rawKey.split(',').map(k => k.trim())
          : rawKey

        // Parse value as string or string[]
        const valOrVals = rawVal.includes(',')
          ? rawVal.split(',').map(v => v.trim())
          : rawVal

        // and POOOSH it in!
        dropdownFilters.push({
          dataKey: keyOrKeys,
          value: valOrVals
        });
      }
    }

    return [...dropdownFilters, ...intervalFilters.filter(f => {
      const iv = f.value as IIntervalValue;
      return iv.min != null || iv.max != null;
    })];
  }

  const updateQueryParameters = (filter: IFilter) => {
    const { dataKey, value } = filter;
    const currentQuery = { ...route.query } as Record<string,string>;    

    const isInterval = typeof value === 'object' && !Array.isArray(value) && ('min' in value || 'max' in value);

    if (isInterval) {
      // Handle interval values
      const { min, max } = value as IIntervalValue;
      const emptyInterval = (min == null) && (max == null);

      if (emptyInterval) {
        // Empty? Delete both min and max
        delete currentQuery[`${dataKey}_min`];
        delete currentQuery[`${dataKey}_max`];
      }
      else {
        // Set min and max if they exist
        if (min != null) {
          currentQuery[`${dataKey}_min`] = String(min);
        }
        else {
          delete currentQuery[`${dataKey}_min`];
        }
        if (max != null) {
          currentQuery[`${dataKey}_max`] = String(max);
        }
        else {
          delete currentQuery[`${dataKey}_max`];
        }
      }

      // always clear single-value key
      delete currentQuery[dataKey]
    }
    // Handle single-value or array values
    else {
      const isEmpty = Array.isArray(value) ? value.length === 0 : value === '';
      if (isEmpty) {
        // If the filter is empty, remove it from the query
        delete currentQuery[dataKey];
      }
      else {
        currentQuery[dataKey] = Array.isArray(value)
          ? value.join(',')
          : String(value);
      }
    }

    router.replace({ query: currentQuery });
  }

  const clearQueryParameters = () => {
    router.replace({ query: {} });
  }

  return {
    mapQueriesToFilter,
    updateQueryParameters,
    clearQueryParameters
  }
}